import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  InputAdornment,
  Divider,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Home as HomeIcon,
  Phone as PhoneIcon,
  Public as PublicIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Cake as CakeIcon,
} from "@mui/icons-material";
import { useFormContext } from "react-hook-form";
import { FullOnboardingForm } from "lib/schema";
import { FormFieldTextField } from "components/shared/components/FormFieldTextField";
import { FormFieldDropdown } from "components/shared/components/FormFieldDropdown";
import { MapboxAddressInput } from "components/shared/components/MapboxAddressInput";
import { COUNTRY_OPTIONS } from "components/Users/constants";
import { useUpdateOnboardingMutation } from "components/Slices/usersApiSlice";

// Image path - save your image to public/images/onboarding-address.png
const ONBOARDING_IMAGE = "/images/onboarding-address.png";

interface ContactInfoStepProps {
  onNext: () => void;
  onBack: () => void;
  isReadOnly?: boolean;
  countryLocked?: boolean;
}

export const ContactInfoStep = ({
  onNext,
  onBack,
  isReadOnly = false,
  countryLocked = false,
}: ContactInfoStepProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { control, trigger, getValues, watch } =
    useFormContext<FullOnboardingForm>();
  const [updateOnboarding, { isLoading }] = useUpdateOnboardingMutation();
  const [error, setError] = useState<string | null>(null);

  const handleNext = async () => {
    if (isReadOnly) {
      onNext();
      return;
    }
    const isValid = await trigger([
      "country",
      "address.line1",
      "address.city",
      "address.state",
      "address.postal_code",
      "phone",
      "dateOfBirth",
    ]);
    if (!isValid) return;

    setError(null);
    try {
      const { address, country, phone, dateOfBirth } = getValues();
      await updateOnboarding({
        address: {
          line1: address.line1,
          line2: address.line2 || undefined,
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
        },
        country,
        phone,
        dateOfBirth,
      }).unwrap();
      onNext();
    } catch (err: any) {
      const message =
        err?.data?.error ||
        err?.data?.message ||
        "Failed to save your contact info. Please try again.";
      setError(message);
    }
  };

  let country = watch("country");

  // Form content component
  const FormContent = (
    <Stack spacing={3}>
      {/* Country Section */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <PublicIcon sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Country
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the country where you reside and where your bank is located.
        </Typography>
        <FormFieldDropdown
          control={control}
          name="country"
          label="Country"
          options={COUNTRY_OPTIONS}
          rules={{ required: "Country is required" }}
          disabled={isReadOnly || countryLocked}
        />
      </Box>
      <Divider />
      {/* Address Section */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <HomeIcon sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Address
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your address is required for identity verification and to comply with
          financial regulations. Start typing to search for your address.
        </Typography>
        <MapboxAddressInput
          control={control}
          mapboxCountry={country}
          namePrefix="address"
          disabled={isReadOnly}
        />
      </Box>
      <Divider />
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <PhoneIcon sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Phone Number
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We'll use this to send you important updates about your circles and
          payments.
        </Typography>
        <FormFieldTextField
          control={control}
          name="phone"
          label="Phone Number"
          placeholder="(416) 555-0123"
          required
          fullWidth
          autoComplete="tel"
          disabled={isReadOnly}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      minWidth: "fit-content",
                    }}
                  >
                    +1
                  </Typography>
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Divider />
      {/* Date of Birth */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CakeIcon sx={{ color: "primary.main", fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Date of Birth
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Required to verify your identity and confirm you meet age
          requirements.
        </Typography>
        <FormFieldTextField
          control={control}
          name="dateOfBirth"
          label="Date of Birth"
          type="date"
          required
          fullWidth
          disabled={isReadOnly}
          inputProps={{
            max: new Date(new Date().setFullYear(new Date().getFullYear() - 18))
              .toISOString()
              .split("T")[0],
            min: new Date(
              new Date().setFullYear(new Date().getFullYear() - 120),
            )
              .toISOString()
              .split("T")[0],
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onBack}
          disabled={isLoading}
          startIcon={<ArrowBackIcon />}
          sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600, flex: 1 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleNext}
          disabled={isLoading}
          endIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <ArrowForwardIcon />
            )
          }
          sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600, flex: 2 }}
        >
          {isReadOnly ? "View Next" : isLoading ? "Saving..." : "Continue"}
        </Button>
      </Box>
    </Stack>
  );

  // Desktop: form with decorative background image
  if (isDesktop) {
    return (
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        <Box
          component="img"
          src={ONBOARDING_IMAGE}
          alt=""
          aria-hidden="true"
          sx={{
            position: "absolute",
            right: -80,
            top: "50%",
            transform: "translateY(-50%)",
            maxWidth: 480,
            height: "auto",
            opacity: 0.1,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
        <Box sx={{ position: "relative", zIndex: 1 }}>{FormContent}</Box>
      </Box>
    );
  }

  return FormContent;
};
